import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import supabase from "../config/supabaseClient";
import '../index2.css';

const GameDetailPage = ({ gameId, onBack }) => {
  const [gameDetails, setGameDetails] = useState(null);
  const [playerCalls, setPlayerCalls] = useState([]);
  const [playerStatuses, setPlayerStatuses] = useState([]);
  const [gameMinutes, setGameMinutes] = useState([]);
  const [gameEvents, setGameEvents] = useState([]);
  const [gameResults, setGameResults] = useState([]);
  const [fetchError, setFetchError] = useState(null);
  const [hasMinutesRecords, setHasMinutesRecords] = useState(false);
  const [hasPlayerStatuses, setHasPlayerStatuses] = useState(false);
  const [hasGameEventsRecords, setHasGameEventsRecords] = useState(false);
  const [hasGameRatings, setHasGameRatings] = useState(false);
  const [gameRatings, setGameRatings] = useState([]);
  const [adversaryInfo, setAdversaryInfo] = useState([]);
  const [hasAdversaryInfo, setHasAdversaryInfo] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchGameDetails = async () => {
      try {
        const { data, error } = await supabase.from('game').select().eq('id', gameId).single();
        if (error) throw error;
        setGameDetails(data);
      } catch (error) {
        setFetchError('Could not fetch game details: ' + (error.message || 'Unknown error'));
      }
    };

    const fetchPlayerCalls = async () => {
      try {
        const { data, error } = await supabase
          .from('call_players_game')
          .select(`player_id, call, players (Name)`)
          .eq('game_id', gameId);
        if (error) throw error;
        setPlayerCalls(data || []);
      } catch (error) {
        setFetchError('Could not fetch player calls: ' + (error.message || 'Unknown error'));
      }
    };

    const fetchGameMinutes = async () => {
      try {
        const { data, error } = await supabase
          .from('game_minutes')
          .select(`player_id, minutes, players (Name)`)
          .eq('game_id', gameId);
        if (error) throw error;
        setGameMinutes(data || []);
        setHasMinutesRecords((data || []).length > 0);
      } catch (error) {
        setFetchError('Could not fetch game minutes: ' + (error.message || 'Unknown error'));
      }
    };

    const fetchGameEvents = async () => {
      try {
        const { data, error } = await supabase
          .from('game_event')
          .select(`player_id, minute, type, players (Name)`)
          .eq('game_id', gameId);
        if (error) throw error;
        setGameEvents(data || []);
        setHasGameEventsRecords((data || []).length > 0);
      } catch (error) {
        setFetchError('Could not fetch game events: ' + (error.message || 'Unknown error'));
      }
    };

    const fetchGameRatings = async () => {
      try {
        const { data } = await supabase.from('game_ratings').select('player_id, rating').eq('game_id', gameId);
        setGameRatings(data || []);
        setHasGameRatings((data || []).length > 0);
      } catch (error) {
        setFetchError('Could not fetch game ratings: ' + (error.message || 'Unknown error'));
      }
    };

    const fetchGameResults = async () => {
      try {
        const { data, error } = await supabase.from('game_results').select().eq('game_id', gameId);
        if (error) throw error;
        setGameResults(data || []);
      } catch (error) {
        setFetchError('Could not fetch game results: ' + (error.message || 'Unknown error'));
      }
    };

    const fetchPlayerStatuses = async () => {
      try {
        const { data, error } = await supabase
          .from('status_player_game')
          .select('player_id, status, position')
          .eq('game_id', gameId);
        if (error) throw error;
        setPlayerStatuses(data || []);
        setHasPlayerStatuses((data || []).length > 0);
      } catch (error) {
        setFetchError('Could not fetch player statuses: ' + (error.message || 'Unknown error'));
      }
    };

    // --- Fetch adversary info: formation_position (e.g. GK, 4-4-2-L1-P1, BENCH-1...) and player_number
    const fetchAdversaryInfo = async () => {
      try {
        const { data, error } = await supabase
          .from('adv_inf')
          .select('formation_position, player_number')
          .eq('game_id', gameId);
        if (error) throw error;
        setAdversaryInfo(data || []);
        setHasAdversaryInfo((data || []).length > 0);
      } catch (error) {
        setFetchError('Could not fetch adversary info: ' + (error.message || 'Unknown error'));
      }
    };

    // Run all fetches
    fetchGameDetails();
    fetchPlayerCalls();
    fetchGameMinutes();
    fetchGameEvents();
    fetchPlayerStatuses();
    fetchGameRatings();
    fetchGameResults();
    fetchAdversaryInfo();
  }, [gameId]);

  const statusMap = playerStatuses.reduce((acc, status) => {
    acc[status.player_id] = status;
    return acc;
  }, {});
  const minutesMap = gameMinutes.reduce((acc, minute) => {
    acc[minute.player_id] = minute;
    return acc;
  }, {});
  const gameResult = gameResults.length > 0 ? gameResults[0] : {};

  const firstTeamPlayers = playerStatuses.filter(s => s.status === 'first');
  const benchPlayers = playerStatuses.filter(s => s.status === 'bench');

  const hasCalls = playerCalls.length > 0;
  const hasResults = gameResults.length > 0;

  // --- Hardcoded positions coordinates by DB position strings ---
  const positionCoordinates = {
    "GK": { top: '90%', left: '45%' },
    "4-3-3-L1-P1": { top: '70%', left: '10%' },
    "4-3-3-L1-P2": { top: '70%', left: '30%' },
    "4-3-3-L1-P3": { top: '70%', left: '50%' },
    "4-3-3-L1-P4": { top: '70%', left: '70%' },
    "4-3-3-L2-P1": { top: '50%', left: '20%' },
    "4-3-3-L2-P2": { top: '50%', left: '45%' },
    "4-3-3-L2-P3": { top: '50%', left: '70%' },
    "4-3-3-L3-P1": { top: '25%', left: '30%' },
    "4-3-3-L3-P2": { top: '25%', left: '60%' },
    "4-3-3-L3-P3": { top: '25%', left: '90%' },

    // 4-4-2
    "4-4-2-L1-P1": { top: '70%', left: '10%' },
    "4-4-2-L1-P2": { top: '70%', left: '30%' },
    "4-4-2-L1-P3": { top: '70%', left: '50%' },
    "4-4-2-L1-P4": { top: '70%', left: '70%' },
    "4-4-2-L2-P1": { top: '50%', left: '20%' },
    "4-4-2-L2-P2": { top: '50%', left: '40%' },
    "4-4-2-L2-P3": { top: '50%', left: '60%' },
    "4-4-2-L2-P4": { top: '50%', left: '80%' },
    "4-4-2-L3-P1": { top: '30%', left: '35%' },
    "4-4-2-L3-P2": { top: '30%', left: '65%' },

    // 3-5-2
    "3-5-2-L1-P1": { top: '70%', left: '20%' },
    "3-5-2-L1-P2": { top: '70%', left: '50%' },
    "3-5-2-L1-P3": { top: '70%', left: '80%' },
    "3-5-2-L2-P1": { top: '55%', left: '10%' },
    "3-5-2-L2-P2": { top: '55%', left: '35%' },
    "3-5-2-L2-P3": { top: '55%', left: '65%' },
    "3-5-2-L2-P4": { top: '55%', left: '90%' },
    "3-5-2-L3-P1": { top: '30%', left: '40%' },
    "3-5-2-L3-P2": { top: '30%', left: '60%' },

    // 3-4-3
    "3-4-3-L1-P1": { top: '70%', left: '20%' },
    "3-4-3-L1-P2": { top: '70%', left: '50%' },
    "3-4-3-L1-P3": { top: '70%', left: '80%' },
    "3-4-3-L2-P1": { top: '50%', left: '25%' },
    "3-4-3-L2-P2": { top: '50%', left: '50%' },
    "3-4-3-L2-P3": { top: '50%', left: '75%' },
    "3-4-3-L3-P1": { top: '30%', left: '20%' },
    "3-4-3-L3-P2": { top: '30%', left: '50%' },
    "3-4-3-L3-P3": { top: '30%', left: '80%' },

    // 4-2-3-1
    "4-2-3-1-L1-P1": { top: '70%', left: '10%' },
    "4-2-3-1-L1-P2": { top: '70%', left: '30%' },
    "4-2-3-1-L1-P3": { top: '70%', left: '50%' },
    "4-2-3-1-L1-P4": { top: '70%', left: '70%' },
    "4-2-3-1-L2-P1": { top: '50%', left: '35%' },
    "4-2-3-1-L2-P2": { top: '50%', left: '50%' },
    "4-2-3-1-L2-P3": { top: '50%', left: '65%' },
    "4-2-3-1-L3-P1": { top: '30%', left: '50%' },

    // 5-3-2
    "5-3-2-L1-P1": { top: '75%', left: '10%' },
    "5-3-2-L1-P2": { top: '75%', left: '30%' },
    "5-3-2-L1-P3": { top: '75%', left: '50%' },
    "5-3-2-L1-P4": { top: '75%', left: '70%' },
    "5-3-2-L1-P5": { top: '75%', left: '90%' },
    "5-3-2-L2-P1": { top: '55%', left: '30%' },
    "5-3-2-L2-P2": { top: '55%', left: '50%' },
    "5-3-2-L2-P3": { top: '55%', left: '70%' },
    "5-3-2-L3-P1": { top: '30%', left: '40%' },
    "5-3-2-L3-P2": { top: '30%', left: '60%' },

    // 5-4-1
    "5-4-1-L1-P1": { top: '75%', left: '10%' },
    "5-4-1-L1-P2": { top: '75%', left: '30%' },
    "5-4-1-L1-P3": { top: '75%', left: '50%' },
    "5-4-1-L1-P4": { top: '75%', left: '70%' },
    "5-4-1-L1-P5": { top: '75%', left: '90%' },
    "5-4-1-L2-P1": { top: '55%', left: '20%' },
    "5-4-1-L2-P2": { top: '55%', left: '40%' },
    "5-4-1-L2-P3": { top: '55%', left: '60%' },
    "5-4-1-L2-P4": { top: '55%', left: '80%' },
    "5-4-1-L3-P1": { top: '30%', left: '50%' },

    // 4-5-1
    "4-5-1-L1-P1": { top: '70%', left: '10%' },
    "4-5-1-L1-P2": { top: '70%', left: '30%' },
    "4-5-1-L1-P3": { top: '70%', left: '50%' },
    "4-5-1-L1-P4": { top: '70%', left: '70%' },
    "4-5-1-L2-P1": { top: '55%', left: '15%' },
    "4-5-1-L2-P2": { top: '55%', left: '35%' },
    "4-5-1-L2-P3": { top: '55%', left: '50%' },
    "4-5-1-L2-P4": { top: '55%', left: '65%' },
    "4-5-1-L2-P5": { top: '55%', left: '85%' },
    "4-5-1-L3-P1": { top: '30%', left: '50%' },

    // 3-6-1
    "3-6-1-L1-P1": { top: '70%', left: '20%' },
    "3-6-1-L1-P2": { top: '70%', left: '40%' },
    "3-6-1-L1-P3": { top: '70%', left: '60%' },
    "3-6-1-L2-P1": { top: '55%', left: '10%' },
    "3-6-1-L2-P2": { top: '55%', left: '25%' },
    "3-6-1-L2-P3": { top: '55%', left: '40%' },
    "3-6-1-L2-P4": { top: '55%', left: '60%' },
    "3-6-1-L2-P5": { top: '55%', left: '75%' },
    "3-6-1-L2-P6": { top: '55%', left: '90%' },
    "3-6-1-L3-P1": { top: '30%', left: '50%' },

    // 4-1-4-1
    "4-1-4-1-L1-P1": { top: '70%', left: '10%' },
    "4-1-4-1-L1-P2": { top: '70%', left: '30%' },
    "4-1-4-1-L1-P3": { top: '70%', left: '50%' },
    "4-1-4-1-L1-P4": { top: '70%', left: '70%' },
    "4-1-4-1-L2-P1": { top: '55%', left: '50%' },
    "4-1-4-1-L3-P1": { top: '40%', left: '15%' },
    "4-1-4-1-L3-P2": { top: '40%', left: '35%' },
    "4-1-4-1-L3-P3": { top: '40%', left: '50%' },
    "4-1-4-1-L3-P4": { top: '40%', left: '65%' },
    "4-1-4-1-L4-P1": { top: '25%', left: '50%' },

    // 4-1-3-2
    "4-1-3-2-L1-P1": { top: '70%', left: '10%' },
    "4-1-3-2-L1-P2": { top: '70%', left: '30%' },
    "4-1-3-2-L1-P3": { top: '70%', left: '50%' },
    "4-1-3-2-L1-P4": { top: '70%', left: '70%' },
    "4-1-3-2-L2-P1": { top: '55%', left: '50%' },
    "4-1-3-2-L3-P1": { top: '40%', left: '30%' },
    "4-1-3-2-L3-P2": { top: '40%', left: '50%' },
    "4-1-3-2-L3-P3": { top: '40%', left: '70%' },
    "4-1-3-2-L4-P1": { top: '25%', left: '40%' },
    "4-1-3-2-L4-P2": { top: '25%', left: '60%' },

    // 4-2-2-2
    "4-2-2-2-L1-P1": { top: '70%', left: '10%' },
    "4-2-2-2-L1-P2": { top: '70%', left: '30%' },
    "4-2-2-2-L1-P3": { top: '70%', left: '50%' },
    "4-2-2-2-L1-P4": { top: '70%', left: '70%' },
    "4-2-2-2-L2-P1": { top: '55%', left: '35%' },
    "4-2-2-2-L2-P2": { top: '55%', left: '55%' },
    "4-2-2-2-L3-P1": { top: '40%', left: '35%' },
    "4-2-2-2-L3-P2": { top: '40%', left: '55%' },

    // Add any additional positions you use...
  };

  // --- helper to detect bench entries in formation_position ---
  const isBenchPosition = (pos) => {
    return typeof pos === 'string' && /^BENCH/i.test(pos.trim());
  };

  // helper to extract bench number (BENCH-1 => 1)
  const benchNumber = (pos) => {
    if (!pos || typeof pos !== 'string') return 999;
    const m = pos.match(/BENCH[-_ ]?(\d+)/i);
    return m ? parseInt(m[1], 10) : 999;
  };

  // split adversary records into main formation and bench
  const advFirst = adversaryInfo.filter(p => !isBenchPosition(p.formation_position));
  const advBench = adversaryInfo
    .filter(p => isBenchPosition(p.formation_position))
    .sort((a, b) => benchNumber(a.formation_position) - benchNumber(b.formation_position));

  return (
    <div className="page game-detail">
      <button onClick={onBack} className="back-button">Back to Games</button>

      {gameResult.my_team && gameResult.adversary_team && (
        <div className="game-results">
          <h2>Game Results</h2>
          <p><strong>Game:</strong> {gameResult.my_team} - {gameResult.adversary_team}</p>
        </div>
      )}

      {!hasCalls && <button onClick={() => navigate(`/add-calls?gameId=${gameId}`)}>Add Calls to Players</button>}
      {!hasMinutesRecords && <button onClick={() => navigate(`/insert-game-minutes?gameId=${gameId}`)}>Insert Game Minutes</button>}
      {!hasPlayerStatuses && <button onClick={() => navigate(`/manage-player-status?gameId=${gameId}`)}>Manage Player Statuses</button>}
      {!hasGameEventsRecords && <button onClick={() => navigate(`/insert-game-events?gameId=${gameId}`)}>Insert Game Events</button>}
      {!hasGameRatings && <button onClick={() => navigate(`/insert-game-ratings?gameId=${gameId}`)}>Insert Game Ratings</button>}
      {!hasResults && <button onClick={() => navigate(`/insert-game-result/${gameId}`)}>Insert Game Result</button>}
      {<button onClick={() => navigate(`/insert-adversary-info?gameId=${gameId}`)}>Insert Adversary Info</button>}

      {fetchError && <p className="error">{fetchError}</p>}

      <button onClick={() => navigate(`/game-stats?gameId=${gameId}`)} className="game-stats-button">
        Manage Game Stats
      </button>

      <button
  onClick={() => navigate(`/insert-adversary-event?gameId=${gameId}`)}
  style={{
    marginTop: "25px",
    padding: "10px 20px",
    background: "#8B0000",
    color: "white",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontWeight: "bold"
  }}
>
  Add Adversary Event
</button>

      {gameDetails && (
        <div className="game-details">
          <h2>Game Details</h2>
          <p><strong>Journey:</strong> {gameDetails.Journey}</p>
          <p><strong>Date:</strong> {gameDetails.Date}</p>
          <p><strong>Time:</strong> {gameDetails.Time}</p>
          <p><strong>Weather:</strong> {gameDetails.Weather}</p>
          <p><strong>Temperature:</strong> {gameDetails.Temperature}°C</p>
          <p><strong>Duration:</strong> {gameDetails.tot_min} minutes</p>
          <p><strong>Formation:</strong> {gameDetails.formation}</p>
        </div>
      )}

      <div style={{ display: 'flex', gap: '40px', flexWrap: 'wrap', justifyContent: 'center' }}>
        {/* Our team */}
        {firstTeamPlayers.length > 0 && (
          <div className="mini-field-container">
            <h3>Our Formation</h3>
            <div className="mini-field" style={{ position: 'relative', width: '400px', height: '600px', background: 'green', border: '2px solid darkgreen', marginTop: '20px' }}>
              {firstTeamPlayers.map(player => {
                const playerInfo = playerCalls.find(p => p.player_id === player.player_id)?.players;
                const coords = positionCoordinates[player.position] || { top: '50%', left: '50%' };
                return (
                  <div key={player.player_id} style={{
                    position: 'absolute',
                    top: coords.top,
                    left: coords.left,
                    transform: 'translate(-50%, -50%)',
                    backgroundColor: 'white',
                    padding: '5px 10px',
                    borderRadius: '5px',
                    textAlign: 'center'
                  }}>
                    {playerInfo?.Name || 'Unknown'}
                  </div>
                );
              })}
            </div>

            {benchPlayers.length > 0 && (
              <div className="bench-container">
                <h4>Bench</h4>
                <div className="bench" style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                  {benchPlayers.map(player => {
                    const playerInfo = playerCalls.find(p => p.player_id === player.player_id)?.players;
                    return (
                      <div key={player.player_id} style={{ background: '#ccc', padding: '5px 10px', borderRadius: '5px' }}>
                        {playerInfo?.Name || 'Unknown'}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Adversary */}
        {hasAdversaryInfo && (
          <div className="mini-field-container">
            <h3>Adversary Formation</h3>
            <div
              className="mini-field"
              style={{
                position: 'relative',
                width: '400px',
                height: '600px',
                background: '#8B0000',
                border: '2px solid darkred',
                marginTop: '20px'
              }}
            >
              {advFirst.map((player, index) => {
                const coords = positionCoordinates[player.formation_position] || { top: '50%', left: '50%' };
                return (
                  <div
                    key={index}
                    style={{
                      position: 'absolute',
                      top: coords.top,
                      left: coords.left,
                      transform: 'translate(-50%, -50%)',
                      backgroundColor: 'white',
                      color: 'black',
                      padding: '5px 10px',
                      borderRadius: '5px',
                      textAlign: 'center'
                    }}
                  >
                    {player.player_number || '?'}
                  </div>
                );
              })}
            </div>

            {advBench.length > 0 && (
              <div className="bench-container">
                <h4>Bench</h4>
                <div className="bench" style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                  {advBench.map((player, index) => (
                    <div key={index} style={{ background: '#ddd', padding: '5px 10px', borderRadius: '5px' }}>
                      {player.player_number || '?'} {player.formation_position ? `(${player.formation_position})` : ''}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default GameDetailPage;
